import { useQuery } from '@tanstack/react-query';
import { Building2, Package, Cpu, Users, Clock, AlertTriangle, HandCoins, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Consolidado = {
  total_bienes: number;
  total_valor_adquisicion: number;
  total_depreciacion_acumulada: number;
  total_valor_libros: number;
  por_tipo: { tipo: string; total: number; valor_adquisicion: number; depreciacion_acumulada: number; valor_libros: number; porcentaje_depreciado: number }[];
  por_unidad_administrativa: { unidad_administrativa_id: string; unidad_nombre: string; total_bienes: number; muebles: number; inmuebles: number; intangibles: number; valor_adquisicion: number; valor_libros: number }[];
  por_resguardante: { responsable_id: string; responsable_nombre: string; total_bienes: number; valor_adquisicion: number; valor_libros: number }[];
  por_antiguedad: { rango: string; total: number; valor_adquisicion: number; valor_libros: number }[];
  bienes_full_depreciados: number;
  prestamos_vigentes: number;
  prestamos_vencidos: number;
};

export function PatrimonioPage() {
  const { data: c } = useQuery({
    queryKey: ['patrimonio-consolidado'],
    queryFn: () => api<Consolidado>('/patrimonio/consolidado'),
  });

  if (!c) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  // Defensas frente a posibles null/undefined del backend
  const porTipo = c.por_tipo ?? [];
  const porUA = c.por_unidad_administrativa ?? [];
  const porResguardante = c.por_resguardante ?? [];
  const porAntiguedad = c.por_antiguedad ?? [];

  const porcenDepre = c.total_valor_adquisicion > 0
    ? (c.total_depreciacion_acumulada * 100 / c.total_valor_adquisicion) : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Recursos Materiales · Patrimonio consolidado</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Tablero patrimonial</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Estado consolidado del patrimonio por tipo, unidad administrativa, resguardante y antigüedad.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI label="Valor en libros"      value={fmt.money(c.total_valor_libros)}         sub={`${c.total_bienes} bienes activos`} accent="brand" icon={<Building2 size={16} />} />
        <KPI label="Valor de adquisición" value={fmt.money(c.total_valor_adquisicion)}   sub={`${porcenDepre.toFixed(1)}% depreciado`}    accent="neutral" icon={<Package size={16} />} />
        <KPI label="Préstamos vigentes"   value={c.prestamos_vigentes.toString()}         sub={`${c.prestamos_vencidos} vencidos`}        accent={c.prestamos_vencidos > 0 ? 'danger' : 'success'} icon={<HandCoins size={16} />} />
        <KPI label="Full depreciados"     value={c.bienes_full_depreciados.toString()}    sub="candidatos a baja"                          accent="warn" icon={<AlertTriangle size={16} />} />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Por tipo de bien" />
          <CardBody className="space-y-3 text-[13px]">
            {porTipo.map((t) => {
              const Icon = t.tipo === 'inmueble' ? Building2 : t.tipo === 'intangible' ? Cpu : Package;
              return (
                <div key={t.tipo} className="flex flex-col gap-1.5 rounded-md border border-border p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={14} className="text-brand-600" />
                      <span className="capitalize font-medium">{t.tipo}</span>
                      <span className="num text-[11px] text-ink-mute">({t.total})</span>
                    </div>
                    <span className="num font-semibold">{fmt.money(t.valor_libros)}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-brand-500" style={{ width: `${Math.min(100, t.porcentaje_depreciado)}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-ink-mute">
                    <span>Adquisición {fmt.money(t.valor_adquisicion)}</span>
                    <span>{t.porcentaje_depreciado}% depreciado</span>
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Por unidad administrativa" subtitle="Quién resguarda qué valor patrimonial" />
          <CardBody className="p-0">
            {porUA.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-ink-mute">Sin datos.</div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Unidad</th>
                    <th className="px-5 py-3 text-right">Muebles</th>
                    <th className="px-5 py-3 text-right">Inmuebles</th>
                    <th className="px-5 py-3 text-right">Intang.</th>
                    <th className="px-5 py-3 text-right">Adquisición</th>
                    <th className="px-5 py-3 text-right">En libros</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {porUA.map((u, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3">
                        <div className="text-ink">{u.unidad_nombre}</div>
                        <div className="num text-[11px] text-ink-mute">{u.total_bienes} bienes</div>
                      </td>
                      <td className="num px-5 py-3 text-right">{u.muebles}</td>
                      <td className="num px-5 py-3 text-right">{u.inmuebles}</td>
                      <td className="num px-5 py-3 text-right">{u.intangibles}</td>
                      <td className="num px-5 py-3 text-right text-ink-mute">{fmt.money(u.valor_adquisicion)}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(u.valor_libros)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Por resguardante" action={<Users size={14} className="text-ink-mute" />} />
          <CardBody className="p-0">
            {porResguardante.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-ink-mute">Sin resguardos activos.</div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Responsable</th>
                    <th className="px-5 py-3 text-right">Bienes</th>
                    <th className="px-5 py-3 text-right">Valor libros</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {porResguardante.map((r, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3">{r.responsable_nombre}</td>
                      <td className="num px-5 py-3 text-right">{r.total_bienes}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(r.valor_libros)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Por antigüedad" action={<Clock size={14} className="text-ink-mute" />} />
          <CardBody className="space-y-3 text-[13px]">
            {porAntiguedad.map((a) => {
              const ancho = c.total_valor_adquisicion > 0
                ? Math.round((a.valor_adquisicion * 100) / c.total_valor_adquisicion) : 0;
              const color = a.rango === '0-1' ? 'bg-emerald-500'
                          : a.rango === '1-5' ? 'bg-sky-500'
                          : a.rango === '5-10' ? 'bg-amber-500'
                          : 'bg-rose-500';
              return (
                <div key={a.rango} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span>{a.rango} años · {a.total} bienes</span>
                    <span className="num font-semibold">{fmt.money(a.valor_libros)}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div className={cn('h-full', color)} style={{ width: `${ancho}%` }} />
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>
      </section>

      <Card>
        <CardHeader title="Ver préstamos activos" action={
          <Link to="/app/bienes/prestamos" className="text-[12px] font-medium text-brand-600 inline-flex items-center gap-1">
            Ver todos <ChevronRight size={14} />
          </Link>
        } />
        <CardBody>
          <p className="text-[13px] text-ink-mute">
            {c.prestamos_vigentes} préstamos activos, de los cuales <strong className="text-danger">{c.prestamos_vencidos}</strong> tienen fecha pactada vencida.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

function KPI({ label, value, sub, accent, icon }: {
  label: string; value: string; sub?: string;
  accent: 'brand'|'success'|'warn'|'danger'|'neutral';
  icon?: React.ReactNode;
}) {
  const cfg = {
    brand: 'before:bg-brand-500',
    success: 'before:bg-emerald-500',
    warn: 'before:bg-amber-500',
    danger: 'before:bg-rose-500',
    neutral: 'before:bg-ink-soft',
  } as const;
  return (
    <div className={cn(
      'relative overflow-hidden rounded-lg border border-border bg-surface p-5 shadow-card',
      'before:absolute before:inset-x-0 before:top-0 before:h-[3px]', cfg[accent],
    )}>
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
        {icon && <span className="text-ink-soft">{icon}</span>}
      </div>
      <div className="num mt-3 text-[22px] font-semibold text-ink">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-ink-mute">{sub}</div>}
    </div>
  );
}
